import { EntryEditError } from '../edits.ts';
import { javaFixed, javaIsBlank, javaRound } from '../javaText.ts';
import { int, scalar, text, transaction, type SqlConnection, type SqlRow } from '../sql.ts';
import { contains, type MapPoint } from './geometry.ts';

/**
 * Drawing on a map (docs/PARITY-STEP-4-PLAN.md section 7), as the desktop's `MapLayer`,
 * `MapAnnotation`, `AnnotationGeometry` and `AtlasService` keep and shape it, checked against
 * `schema/fixtures/drawing-cases.json`:
 * - a map's drawings are on named layers, bottom first; a new layer goes on top and starts
 *   hidden from players, so nothing drawn is shown by accident;
 * - a drawing is a pen stroke, a line, an arrow, a box, an ellipse or text, its points in
 *   the map image's pixels (stored to one place), with its stroke colour, width and dashes,
 *   a fill for a box or ellipse, and a text's words and size;
 * - Undo and Redo put a map's drawings back to how they were (`restoreAnnotations`).
 */

export const DRAWING_KINDS = ['PEN', 'LINE', 'ARROW', 'RECTANGLE', 'ELLIPSE', 'TEXT'] as const;
export type DrawingKind = (typeof DRAWING_KINDS)[number];
export const DRAWING_LIMITS = { layerName: 200, text: 1_000, points: 5_000, width: 200, textSize: 1_000 } as const;
/** `MapAnnotation.DEFAULT_COLOR`. */
export const DRAWING_COLOR = '#C0392B';

export interface MapLayer {
  readonly id: string;
  readonly mapId: string;
  readonly name: string;
  readonly position: number;
  readonly playersCanSee: boolean;
  readonly created: string;
}

export interface DrawingStyle {
  readonly strokeColor: string;
  readonly strokeWidth: number;
  readonly dashed: boolean;
  /** A box's or ellipse's fill; null for none. */
  readonly fillColor: string | null;
  readonly fillOpacity: number;
  readonly textSize: number;
}

/** `MapAnnotation.Style.DEFAULT`. */
export const DEFAULT_STYLE: DrawingStyle = { strokeColor: DRAWING_COLOR, strokeWidth: 4, dashed: false, fillColor: null, fillOpacity: 0.25, textSize: 28 };

export interface MapDrawing extends DrawingStyle {
  readonly id: string;
  readonly layerId: string;
  readonly kind: DrawingKind;
  /** A stroke's or route's points; a box's or ellipse's two opposite corners; a text's top-left corner. */
  readonly points: readonly MapPoint[];
  readonly text: string;
  readonly created: string;
  readonly modified: string;
}

export const fillable = (kind: DrawingKind) => kind === 'RECTANGLE' || kind === 'ELLIPSE';

// ---- reading ----

export function listLayers(conn: SqlConnection, mapId: string): MapLayer[] {
  return conn.all('SELECT id, map_id, name, position, players_can_see, created FROM map_layers WHERE map_id = ? ORDER BY position, created', [mapId]).map((r) => {
    const name = text(r['name']);
    return { id: text(r['id']), mapId: text(r['map_id']), name: javaIsBlank(name) ? 'Drawings' : name.trim(), position: int(r['position']), playersCanSee: int(r['players_can_see']) !== 0, created: text(r['created']) };
  });
}

/** Every drawing on a map, bottom layer first. */
export function listDrawings(conn: SqlConnection, mapId: string): MapDrawing[] {
  return conn
    .all(
      `SELECT a.id, a.layer_id, a.kind, a.points, a.stroke_color, a.stroke_width, a.dashed, a.fill_color, a.fill_opacity, a.text, a.text_size, a.created, a.modified
       FROM map_annotations a JOIN map_layers l ON l.id = a.layer_id
       WHERE l.map_id = ? ORDER BY l.position, l.created, a.created`,
      [mapId],
    )
    .map(drawingOf);
}

function drawingOf(r: SqlRow): MapDrawing {
  const kindText = text(r['kind']).toUpperCase();
  const kind = (DRAWING_KINDS as readonly string[]).includes(kindText) ? (kindText as DrawingKind) : 'LINE';
  const stroke = text(r['stroke_color']);
  const fill = r['fill_color'] === null || r['fill_color'] === undefined ? '' : text(r['fill_color']);
  const width = Number(r['stroke_width'] ?? 0);
  const size = Number(r['text_size'] ?? 0);
  const created = text(r['created']);
  return {
    id: text(r['id']),
    layerId: text(r['layer_id']),
    kind,
    points: parseDrawingPoints(text(r['points'])),
    strokeColor: javaIsBlank(stroke) ? DRAWING_COLOR : stroke,
    strokeWidth: width <= 0 ? 4 : width,
    dashed: int(r['dashed']) !== 0,
    fillColor: javaIsBlank(fill) ? null : fill,
    fillOpacity: Math.max(0, Math.min(1, Number(r['fill_opacity'] ?? 0))),
    text: text(r['text']),
    textSize: size <= 0 ? 24 : size,
    created,
    modified: text(r['modified']) || created,
  };
}

/** `MapAnnotation.pointsText`: "x,y;x,y" to one place. */
export function drawingPointsText(points: readonly MapPoint[]): string {
  return points.map((p) => `${javaFixed(p.x, 1)},${javaFixed(p.y, 1)}`).join(';');
}

/** `MapAnnotation.parsePoints`: a damaged point is skipped, not the whole drawing. */
export function parseDrawingPoints(stored: string): MapPoint[] {
  if (stored.trim() === '') return [];
  return stored.split(';').flatMap((pair) => {
    const xy = pair.split(',');
    if (xy.length !== 2) return [];
    const [x, y] = xy.map((v) => (/^\s*[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?\s*$/.test(v) ? Number(v) : Number.NaN));
    return Number.isFinite(x) && Number.isFinite(y) ? [{ x: x!, y: y! }] : [];
  });
}

// ---- shapes (`AnnotationGeometry`) ----

/** `AnnotationGeometry.n`: to one place, whole numbers whole. */
function n(value: number): string {
  const rounded = javaRound(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : javaFixed(rounded, 1);
}

const arrowLength = (d: Pick<MapDrawing, 'strokeWidth'>) => Math.max(d.strokeWidth * 4, 12);
const distanceBetween = (a: MapPoint, b: MapPoint) => Math.hypot(a.x - b.x, a.y - b.y);

/** [minX, minY, maxX, maxY] of two opposite corners. */
function box(a: MapPoint, b: MapPoint): [number, number, number, number] {
  return [Math.min(a.x, b.x), Math.min(a.y, b.y), Math.max(a.x, b.x), Math.max(a.y, b.y)];
}

function polyline(p: readonly MapPoint[]): string {
  return `M${n(p[0]!.x)} ${n(p[0]!.y)}` + p.slice(1).map((q) => `L${n(q.x)} ${n(q.y)}`).join('');
}

/** Quadratic curves through the midpoints, so a freehand stroke reads as one smooth line. */
function smooth(p: readonly MapPoint[]): string {
  if (p.length < 3) return polyline(p);
  let d = `M${n(p[0]!.x)} ${n(p[0]!.y)}`;
  for (let i = 1; i < p.length - 1; i++) {
    const mx = (p[i]!.x + p[i + 1]!.x) / 2;
    const my = (p[i]!.y + p[i + 1]!.y) / 2;
    d += `Q${n(p[i]!.x)} ${n(p[i]!.y)} ${n(mx)} ${n(my)}`;
  }
  const last = p.at(-1)!;
  return `${d}L${n(last.x)} ${n(last.y)}`;
}

/** The points with the last segment pulled back by `by`, so an arrow's line ends under its head. */
function shortened(p: readonly MapPoint[], by: number): readonly MapPoint[] {
  if (p.length < 2) return p;
  const tip = p.at(-1)!;
  const from = p.at(-2)!;
  const d = distanceBetween(tip, from);
  if (d <= by) return p;
  const t = (d - by) / d;
  return [...p.slice(0, -1), { x: from.x + (tip.x - from.x) * t, y: from.y + (tip.y - from.y) * t }];
}

/** The drawing's stroke (and fill) outline as SVG path data; text has none. */
export function drawingPath(d: Pick<MapDrawing, 'kind' | 'points' | 'strokeWidth'>): string {
  const p = d.points;
  if (p.length === 0) return '';
  switch (d.kind) {
    case 'PEN':
      return smooth(p);
    case 'LINE':
      return polyline(p);
    case 'ARROW':
      return polyline(shortened(p, arrowLength(d) * 0.7));
    case 'RECTANGLE': {
      if (p.length < 2) return '';
      const [x0, y0, x1, y1] = box(p[0]!, p[1]!);
      return `M${n(x0)} ${n(y0)}H${n(x1)}V${n(y1)}H${n(x0)}Z`;
    }
    case 'ELLIPSE': {
      if (p.length < 2) return '';
      const [x0, y0, x1, y1] = box(p[0]!, p[1]!);
      const rx = (x1 - x0) / 2;
      const ry = (y1 - y0) / 2;
      const cy = y0 + ry;
      return `M${n(x0)} ${n(cy)}A${n(rx)} ${n(ry)} 0 1 1 ${n(x1)} ${n(cy)}A${n(rx)} ${n(ry)} 0 1 1 ${n(x0)} ${n(cy)}Z`;
    }
    case 'TEXT':
      return '';
  }
}

/** An arrow's head, a filled triangle; empty for anything else. */
export function arrowHeadPath(d: Pick<MapDrawing, 'kind' | 'points' | 'strokeWidth'>): string {
  const p = d.points;
  if (d.kind !== 'ARROW' || p.length < 2) return '';
  const tip = p.at(-1)!;
  const from = p.at(-2)!;
  const length = arrowLength(d);
  const dx = tip.x - from.x;
  const dy = tip.y - from.y;
  const dist = Math.hypot(dx, dy);
  if (dist === 0) return '';
  const ux = dx / dist;
  const uy = dy / dist;
  const bx = tip.x - ux * length;
  const by = tip.y - uy * length;
  const half = length * 0.55;
  return `M${n(tip.x)} ${n(tip.y)}L${n(bx - uy * half)} ${n(by + ux * half)}L${n(bx + uy * half)} ${n(by - ux * half)}Z`;
}

/** How much of a text's size each character is taken to be wide. */
const CHAR_WIDTH = 0.56;
const textWidth = (d: Pick<MapDrawing, 'text' | 'textSize'>) => Math.max(1, d.text.length) * d.textSize * CHAR_WIDTH;

/** [minX, minY, maxX, maxY], stroke included. */
export function drawingBounds(d: Pick<MapDrawing, 'kind' | 'points' | 'strokeWidth' | 'text' | 'textSize'>): [number, number, number, number] {
  if (d.kind === 'TEXT') {
    const at = d.points[0] ?? { x: 0, y: 0 };
    return [at.x, at.y, at.x + textWidth(d), at.y + d.textSize];
  }
  const xs = d.points.map((p) => p.x);
  const ys = d.points.map((p) => p.y);
  const pad = d.kind === 'ARROW' ? arrowLength(d) / 2 : d.strokeWidth / 2;
  return [Math.min(...xs) - pad, Math.min(...ys) - pad, Math.max(...xs) + pad, Math.max(...ys) + pad];
}

function segmentDistance(a: MapPoint, b: MapPoint, point: MapPoint): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) return distanceBetween(a, point);
  const t = Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / lengthSquared));
  return Math.hypot(point.x - (a.x + t * dx), point.y - (a.y + t * dy));
}

function polylineDistance(p: readonly MapPoint[], point: MapPoint): number {
  if (p.length === 1) return distanceBetween(p[0]!, point);
  let best = Number.MAX_VALUE;
  for (let i = 0; i + 1 < p.length; i++) best = Math.min(best, segmentDistance(p[i]!, p[i + 1]!, point));
  return best;
}

function ellipseOutline([x0, y0, x1, y1]: [number, number, number, number]): MapPoint[] {
  const cx = (x0 + x1) / 2;
  const cy = (y0 + y1) / 2;
  const rx = (x1 - x0) / 2;
  const ry = (y1 - y0) / 2;
  return Array.from({ length: 49 }, (_, i) => {
    const a = (Math.PI * 2 * i) / 48;
    return { x: cx + rx * Math.cos(a), y: cy + ry * Math.sin(a) };
  });
}

/** How far a point is from the drawing, in map pixels: 0 on its line, within a filled shape, or inside text. */
export function drawingDistance(d: Pick<MapDrawing, 'kind' | 'points' | 'strokeWidth' | 'fillColor' | 'text' | 'textSize'>, point: MapPoint): number {
  const p = d.points;
  if (p.length === 0) return Number.MAX_VALUE;
  const half = d.strokeWidth / 2;
  switch (d.kind) {
    case 'PEN':
    case 'LINE':
    case 'ARROW':
      return Math.max(0, polylineDistance(p, point) - half);
    case 'RECTANGLE': {
      if (p.length < 2) return Number.MAX_VALUE;
      const [x0, y0, x1, y1] = box(p[0]!, p[1]!);
      const corners = [{ x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }, { x: x0, y: y0 }];
      const inside = point.x >= x0 && point.x <= x1 && point.y >= y0 && point.y <= y1;
      return inside && d.fillColor !== null ? 0 : Math.max(0, polylineDistance(corners, point) - half);
    }
    case 'ELLIPSE': {
      if (p.length < 2) return Number.MAX_VALUE;
      const outline = ellipseOutline(box(p[0]!, p[1]!));
      return contains(outline, point) && d.fillColor !== null ? 0 : Math.max(0, polylineDistance(outline, point) - half);
    }
    case 'TEXT': {
      const [x0, y0, x1, y1] = drawingBounds(d);
      return Math.hypot(Math.max(x0 - point.x, 0, point.x - x1), Math.max(y0 - point.y, 0, point.y - y1));
    }
  }
}

/** A freehand stroke with the points that add nothing removed (Ramer-Douglas-Peucker). */
export function simplifyStroke(points: readonly MapPoint[], tolerance: number): MapPoint[] {
  if (points.length < 3) return [...points];
  const keep = points.map((_, i) => i === 0 || i === points.length - 1);
  const walk = (from: number, to: number) => {
    let worst = 0;
    let index = -1;
    for (let i = from + 1; i < to; i++) {
      const d = segmentDistance(points[from]!, points[to]!, points[i]!);
      if (d > worst) {
        worst = d;
        index = i;
      }
    }
    if (index >= 0 && worst > tolerance) {
      keep[index] = true;
      walk(from, index);
      walk(index, to);
    }
  };
  walk(0, points.length - 1);
  return points.filter((_, i) => keep[i]);
}

/** With Shift: a line snapped to the nearest 45 degrees. */
export function constrainLine(start: MapPoint, end: MapPoint): MapPoint {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.hypot(dx, dy);
  const angle = javaRound(Math.atan2(dy, dx) / (Math.PI / 4)) * (Math.PI / 4);
  return { x: start.x + length * Math.cos(angle), y: start.y + length * Math.sin(angle) };
}

/** With Shift: a square or circle. */
export function constrainBox(start: MapPoint, end: MapPoint): MapPoint {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const side = Math.max(Math.abs(dx), Math.abs(dy));
  return { x: start.x + (dx < 0 ? -side : side), y: start.y + (dy < 0 ? -side : side) };
}

// ---- layers ----

/** A new layer on top of the map's others, hidden from players until chosen otherwise. */
export function createLayer(conn: SqlConnection, mapId: string, name: string, id: string = crypto.randomUUID(), now: string = new Date().toISOString()): string {
  const clean = layerName(name);
  transaction(conn, () => {
    if (int(scalar(conn, 'SELECT COUNT(*) FROM atlas_maps WHERE id = ?', [mapId])) === 0) throw new EntryEditError('not_found', 'That map no longer exists.');
    const top = int(scalar(conn, 'SELECT COALESCE(MAX(position), -1) FROM map_layers WHERE map_id = ?', [mapId])) + 1;
    conn.run('INSERT INTO map_layers (id, map_id, name, position, players_can_see, created) VALUES (?, ?, ?, ?, 0, ?)', [id, mapId, clean, top, now]);
  });
  return id;
}

export function updateLayer(conn: SqlConnection, id: string, name: string, playersCanSee: boolean): void {
  const clean = layerName(name);
  transaction(conn, () => {
    mustExist(conn, 'map_layers', id, 'That layer no longer exists.');
    conn.run('UPDATE map_layers SET name = ?, players_can_see = ? WHERE id = ?', [clean, playersCanSee ? 1 : 0, id]);
  });
}

/** Swaps a layer with the one above (`up`) or below, renumbering the stack as the desktop does. */
export function moveLayer(conn: SqlConnection, id: string, up: boolean): void {
  transaction(conn, () => {
    const row = conn.all('SELECT map_id FROM map_layers WHERE id = ?', [id])[0];
    if (!row) throw new EntryEditError('not_found', 'That layer no longer exists.');
    const layers = listLayers(conn, text(row['map_id']));
    const index = layers.findIndex((l) => l.id === id);
    const other = index + (up ? 1 : -1);
    if (other < 0 || other >= layers.length) return;
    [layers[index], layers[other]] = [layers[other]!, layers[index]!];
    layers.forEach((l, i) => conn.run('UPDATE map_layers SET position = ? WHERE id = ?', [i, l.id]));
  });
}

/** Deletes a layer and every drawing on it; returns how many drawings went. */
export function deleteLayer(conn: SqlConnection, id: string): number {
  return transaction(conn, () => {
    mustExist(conn, 'map_layers', id, 'That layer no longer exists.');
    const drawings = int(scalar(conn, 'SELECT COUNT(*) FROM map_annotations WHERE layer_id = ?', [id]));
    conn.run('DELETE FROM map_layers WHERE id = ?', [id]);
    return drawings;
  });
}

// ---- drawings ----

export interface DrawingInput extends DrawingStyle {
  readonly id: string;
  readonly layerId: string;
  readonly kind: DrawingKind;
  readonly points: readonly MapPoint[];
  readonly text: string;
}

/** Saves a drawing, new or changed (`saveAnnotation`), on one of the map's layers. */
export function saveDrawing(conn: SqlConnection, mapId: string, drawing: DrawingInput, now: string = new Date().toISOString()): void {
  transaction(conn, () => saveOne(conn, mapId, drawing, now));
}

function saveOne(conn: SqlConnection, mapId: string, d: DrawingInput, now: string): void {
  if (!DRAWING_KINDS.includes(d.kind)) throw new EntryEditError('invalid', 'That is not a kind of drawing.');
  if (d.points.length === 0 || d.points.length > DRAWING_LIMITS.points) throw new EntryEditError('invalid', 'That drawing has no points, or too many.');
  for (const p of d.points) if (!Number.isFinite(p.x) || !Number.isFinite(p.y) || Math.abs(p.x) > 1e6 || Math.abs(p.y) > 1e6) throw new EntryEditError('invalid', 'That is not a place on the map.');
  for (const c of [d.strokeColor, ...(d.fillColor === null ? [] : [d.fillColor])]) if (!/^#[0-9A-Fa-f]{6}$/.test(c)) throw new EntryEditError('invalid', 'That is not a colour.');
  if (!(d.strokeWidth > 0 && d.strokeWidth <= DRAWING_LIMITS.width) || !(d.textSize > 0 && d.textSize <= DRAWING_LIMITS.textSize)) throw new EntryEditError('invalid', 'That size is out of range.');
  if (d.text.length > DRAWING_LIMITS.text) throw new EntryEditError('invalid', `A drawing's text can be at most ${DRAWING_LIMITS.text.toLocaleString('en')} characters.`);
  const layer = conn.all('SELECT map_id FROM map_layers WHERE id = ?', [d.layerId])[0];
  if (!layer || text(layer['map_id']) !== mapId) throw new EntryEditError('not_found', 'That layer is no longer on this map.');
  const existing = conn.all('SELECT a.created, l.map_id FROM map_annotations a JOIN map_layers l ON l.id = a.layer_id WHERE a.id = ?', [d.id])[0];
  if (existing && text(existing['map_id']) !== mapId) throw new EntryEditError('invalid', 'That drawing is on another map.');
  conn.run(
    `INSERT INTO map_annotations (id, layer_id, kind, points, stroke_color, stroke_width, dashed, fill_color, fill_opacity, text, text_size, created, modified)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
     ON CONFLICT(id) DO UPDATE SET layer_id = excluded.layer_id, kind = excluded.kind, points = excluded.points,
       stroke_color = excluded.stroke_color, stroke_width = excluded.stroke_width, dashed = excluded.dashed, fill_color = excluded.fill_color,
       fill_opacity = excluded.fill_opacity, text = excluded.text, text_size = excluded.text_size, modified = excluded.modified`,
    [
      d.id,
      d.layerId,
      d.kind,
      drawingPointsText(d.points),
      d.strokeColor,
      d.strokeWidth,
      d.dashed ? 1 : 0,
      fillable(d.kind) ? d.fillColor : null,
      Math.max(0, Math.min(1, d.fillOpacity)),
      d.text,
      d.textSize,
      existing ? text(existing['created']) : now,
      now,
    ],
  );
}

export function deleteDrawing(conn: SqlConnection, mapId: string, id: string): void {
  transaction(conn, () => {
    conn.run('DELETE FROM map_annotations WHERE id = ? AND layer_id IN (SELECT id FROM map_layers WHERE map_id = ?)', [id, mapId]);
  });
}

/**
 * Puts a map's drawings back to exactly `wanted` (`restoreAnnotations`), for Undo and Redo:
 * any drawing not in it is removed, and each that is is saved, on a layer that still exists.
 */
export function restoreDrawings(conn: SqlConnection, mapId: string, wanted: readonly DrawingInput[], now: string = new Date().toISOString()): void {
  transaction(conn, () => {
    const keep = new Set(wanted.map((d) => d.id));
    const layers = new Set(listLayers(conn, mapId).map((l) => l.id));
    for (const existing of listDrawings(conn, mapId)) if (!keep.has(existing.id)) conn.run('DELETE FROM map_annotations WHERE id = ?', [existing.id]);
    for (const d of wanted) if (layers.has(d.layerId)) saveOne(conn, mapId, d, now);
  });
}

// ---- helpers ----

function layerName(name: string): string {
  if (name.length > DRAWING_LIMITS.layerName) throw new EntryEditError('invalid', `A layer's name can be at most ${DRAWING_LIMITS.layerName} characters.`);
  return javaIsBlank(name) ? 'Drawings' : name.trim().replace(/[\r\n]+/g, ' ');
}

/** `table` is always one of this module's literals, never input. */
function mustExist(conn: SqlConnection, table: string, id: string, message: string): void {
  if (int(scalar(conn, `SELECT COUNT(*) FROM ${table} WHERE id = ?`, [id])) === 0) throw new EntryEditError('not_found', message);
}
